import DesignTokens from '@tableau/design-tokens';
import { TabStyles } from '@tableau/widgets';

import { CommonStyles } from '~commonInfra/styles/CommonStyles';
import { cssClass } from '~src/style';
import { applyColor } from '~src/style/ColorService';

const cssPrefix = 'AdminProfile';

export namespace AdminProfileStyles {
  /** Main container for admin profiles page */
  export const Container = cssClass(cssPrefix, 'container', {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  });

  /** Header section */
  export const Header = cssClass(cssPrefix, 'header', {
    padding: `${TabStyles.Spacing.LargeSpace}px ${TabStyles.Spacing.ExtraLargeSpace}px`,
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L3)}`,
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.default, TabStyles.Colors.White),
  });

  export const HeaderTitle = cssClass(cssPrefix, 'header-title', {
    fontSize: '24px',
    fontWeight: 500,
    color: TabStyles.Colors.F9,
    margin: 0,
  });

  export const HeaderDescription = cssClass(cssPrefix, 'header-description', {
    fontSize: '14px',
    color: TabStyles.Colors.F7,
    marginTop: TabStyles.Spacing.SmallSpace,
  });

  /** Profile list item */
  export const ProfileListItem = cssClass(cssPrefix, 'profile-list-item', {
    padding: `${TabStyles.Spacing.MediumSpace}px ${TabStyles.Spacing.LargeSpace}px`,
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L3)}`,
    cursor: 'pointer',
    transition: 'all 0.2s ease',

    $nest: {
      '&:hover': {
        backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, CommonStyles.Colors.Grey245),
      },
      '&:focus': {
        outline: 'none',
      },
    },
  });

  export const ProfileListItemSelected = cssClass(cssPrefix, 'profile-list-item-selected', {
    backgroundColor: applyColor(DesignTokens.color.navigation.tabs.vertical.default.selected, 'rgb(241,241,241)'),

    $nest: {
      '&:hover': {
        backgroundColor: applyColor(DesignTokens.color.navigation.tabs.vertical.default.selected, 'rgb(241,241,241)'),
      },
    },
  });

  export const ProfileListItemName = cssClass(cssPrefix, 'profile-list-item-name', {
    fontSize: '14px',
    fontWeight: 500,
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Colors.F9),
    marginBottom: TabStyles.Spacing.SmallSpace,
  });

  export const ProfileListItemMeta = cssClass(cssPrefix, 'profile-list-item-meta', {
    fontSize: '12px',
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F7),
    display: 'flex',
    gap: TabStyles.Spacing.MediumSpace,
  });

  export const ProfileListItemMetaItem = cssClass(cssPrefix, 'profile-list-item-meta-item', {
    display: 'inline-flex',
    alignItems: 'center',
    gap: TabStyles.Spacing.SmallSpace,
  });

  /** Action menu positioning within list items */
  export const ProfileListItemContainer: React.CSSProperties = {
    position: 'relative',
  };

  export const ActionMenuContainer: React.CSSProperties = {
    position: 'absolute',
    top: 8,
    right: 8,
  };

  /** Detail panel */
  export const DetailHeader = cssClass(cssPrefix, 'detail-header', {
    marginBottom: TabStyles.Spacing.ExtraLargeSpace,
  });

  export const DetailTitle = cssClass(cssPrefix, 'detail-title', {
    fontSize: '20px',
    fontWeight: 500,
    color: applyColor(DesignTokens.color.font.default.default, TabStyles.Colors.F9),
    marginBottom: TabStyles.Spacing.SmallSpace,
  });

  export const DetailDescription = cssClass(cssPrefix, 'detail-description', {
    fontSize: '14px',
    color: TabStyles.Colors.F7,
    marginBottom: TabStyles.Spacing.LargeSpace,
  });

  export const DetailSection = cssClass(cssPrefix, 'detail-section', {
    marginBottom: TabStyles.Spacing.ExtraLargeSpace,
  });

  export const DetailSectionTitle = cssClass(cssPrefix, 'detail-section-title', {
    fontSize: '16px',
    fontWeight: 500,
    color: TabStyles.Colors.F9,
    marginBottom: TabStyles.Spacing.MediumSpace,
  });

  /** Empty states */
  export const EmptyState = cssClass(cssPrefix, 'empty-state', {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
    textAlign: 'center',
    color: TabStyles.Colors.F7,
    padding: TabStyles.Spacing.ExtraLargeSpace,
  });

  export const EmptyStateIcon = cssClass(cssPrefix, 'empty-state-icon', {
    fontSize: '48px',
    marginBottom: TabStyles.Spacing.LargeSpace,
    color: TabStyles.Colors.F5,
  });

  export const EmptyStateTitle = cssClass(cssPrefix, 'empty-state-title', {
    fontSize: '16px',
    fontWeight: 500,
    color: TabStyles.Colors.F8,
    marginBottom: TabStyles.Spacing.SmallSpace,
  });

  export const EmptyStateDescription = cssClass(cssPrefix, 'empty-state-description', {
    fontSize: '14px',
    color: TabStyles.Colors.F7,
  });

  /** Tab content */
  export const TabContent = cssClass(cssPrefix, 'tab-content', {
    height: '100%',
    width: '100%',
    overflow: 'auto',
    boxSizing: 'border-box',
  });

  /** Permissions tab: fill available height, scroll inside the list */
  export const PermissionsTabContent = cssClass(cssPrefix, 'permissions-tab-content', {
    height: '100%',
    width: '100%',
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  });

  /** Permissions container override: fill height instead of dialog's 300px maxHeight */
  export const PermissionsContainerFull = cssClass(cssPrefix, 'permissions-container-full', {
    flex: 1,
    minHeight: 0,
    maxHeight: 'none',
  });

  /** Wrapper for list + search so search stays fixed and only list scrolls */
  export const ListWithSearchWrapper = cssClass(cssPrefix, 'list-with-search-wrapper', {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflow: 'hidden',
  });

  /** Scrollable area for list items (search stays above, outside this). No horizontal padding - fills full width. */
  export const ListScrollArea = cssClass(cssPrefix, 'list-scroll-area', {
    flex: 1,
    minHeight: 0,
    overflow: 'auto',
  });

  /** Search box - has horizontal padding (24px each side) so input is inset; fills width of parent minus 48px */
  export const SearchBox = cssClass(cssPrefix, 'search-box', {
    flexShrink: 0,
    marginBottom: TabStyles.Spacing.LargeSpace,
    width: '100%',
    paddingLeft: TabStyles.Spacing.LargeSpace,
    paddingRight: TabStyles.Spacing.LargeSpace,
    boxSizing: 'border-box',

    $nest: {
      /* Make TextFieldWidget and its input fill the available width (parent minus 48px padding) */
      '& > *': { width: '100%', minWidth: 0 },
      '& input': { width: '100%', minWidth: 0, boxSizing: 'border-box' },
    },
  });

  /** Badge */
  export const Badge = cssClass(cssPrefix, 'badge', {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `2px ${TabStyles.Spacing.SmallSpace}px`,
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, TabStyles.Colors.L3),
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: 500,
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F8),
  });

  export const SystemBadge = cssClass(cssPrefix, 'system-badge', {
    backgroundColor: TabStyles.Colors.ActionLight,
    color: TabStyles.Colors.ActionDark,
  });

  /** List view styles - matching product's standard list view */
  export const ListViewContainer = cssClass(cssPrefix, 'list-view-container', {
    width: '100%',
    height: '100%',
    overflow: 'auto',
  });

  export const ListViewTable = cssClass(cssPrefix, 'list-view-table', {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  });

  export const ListViewHeader = cssClass(cssPrefix, 'list-view-header', {
    padding: `${TabStyles.Spacing.SmallSpace}px ${TabStyles.Spacing.MediumSpace}px`,
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 500,
    color: applyColor(DesignTokens.color.font.default.secondary, TabStyles.Colors.F7),
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L3)}`,
    backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, TabStyles.Colors.L1),
    height: '32px',
  });

  export const ListViewRow = cssClass(cssPrefix, 'list-view-row', {
    borderBottom: `1px solid ${applyColor(DesignTokens.color.elevation.border.default.subtle, TabStyles.Colors.L2)}`,
    transition: 'background-color 0.15s ease',

    $nest: {
      '&:hover': {
        backgroundColor: applyColor(DesignTokens.color.elevation.surface.default.subtle, TabStyles.Colors.L1),
      },
    },
  });

  export const ListViewCell = cssClass(cssPrefix, 'list-view-cell', {
    padding: `${TabStyles.Spacing.MediumSpace}px ${TabStyles.Spacing.MediumSpace}px`,
    fontSize: '13px',
    color: TabStyles.Colors.F8,
    height: '44px',
    verticalAlign: 'middle',
  });

  export const ListViewCellPrimary = cssClass(cssPrefix, 'list-view-cell-primary', {
    fontWeight: 500,
    color: TabStyles.Colors.F9,
  });

  export const ListViewCellSecondary = cssClass(cssPrefix, 'list-view-cell-secondary', {
    color: TabStyles.Colors.F7,
  });

  export const ListViewLink = cssClass(cssPrefix, 'list-view-link', {
    color: TabStyles.Colors.ActionDark,
    textDecoration: 'none',

    $nest: {
      '&:hover': {
        textDecoration: 'underline',
      },
    },
  });

  export const ListViewRoleBadge = cssClass(cssPrefix, 'list-view-role-badge', {
    display: 'inline-flex',
    alignItems: 'center',
    padding: `2px ${TabStyles.Spacing.SmallSpace}px`,
    borderRadius: '3px',
    fontSize: '12px',
    fontWeight: 500,
    backgroundColor: '#e3f2fd', // Light blue for badge - no design token available
    color: '#1565c0', // Blue text - no design token available
  });

  /** AdminProfilesSpace header styles */
  export const SpaceHeaderCount = cssClass(cssPrefix, 'space-header-count', {
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.AccessibleSecondaryTextColor),
    fontSize: '14px',
  });

  export const SpaceHeaderDescription = cssClass(cssPrefix, 'space-header-description', {
    fontSize: '14px',
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.AccessibleSecondaryTextColor),
    marginTop: '8px',
    marginBottom: '4px',
    lineHeight: '1.5',
  });

  /** Intro text for Admin Profile Sites tab toolbar */
  export const SitesIntroText = cssClass(cssPrefix, 'sites-intro-text', {
    fontSize: '14px',
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.AccessibleSecondaryTextColor),
  });

  export const SpaceContentWrapper = cssClass(cssPrefix, 'space-content-wrapper', {
    flex: 1,
    minHeight: 0,
  });

  /** AdminProfileHeader specific styles */
  export const HeaderMetadata = cssClass(cssPrefix, 'header-metadata', {
    fontSize: '12px',
    color: applyColor(DesignTokens.color.font.default.secondary, CommonStyles.Colors.Grey102),
    marginTop: '8px',
  });

  export const HeaderBadgeSpacing = cssClass(cssPrefix, 'header-badge-spacing', {
    marginLeft: '8px',
  });

  export const MetadataUserLink = cssClass(cssPrefix, 'metadata-user-link', {
    color: DesignTokens.color.link.inline.default.default,
    fontWeight: 'bold',
    letterSpacing: '0.2px',
    padding: '1px',
    textDecoration: 'none',
    cursor: 'pointer',
    $nest: {
      '&:visited': {
        color: DesignTokens.color.link.inline.default.default,
      },
      '&:hover': {
        color: DesignTokens.color.link.inline.default.hover,
        textDecoration: 'underline',
      },
    },
  });

}
